import { Draft, PayloadAction } from '@reduxjs/toolkit'
import { map, filter, findIndex, set } from 'lodash'

import {
  updateObjects,
  createInitialCommentObject,
  undoRedoObject,
  updateAppendComment,
  updateDeleteComment,
  updateToggleConfirmComment,
  updateConfirmAllComments,
  updateLabelAssistantStatus,
  updateLabelingItemStatus,
  addSnapshot
} from '@/stores/slices/Labeling/reducers/common'
import { transformAnnotObjects } from '@/stores/slices/Labeling/utils'
import { LabelingState, LabelingObjectData } from '@/stores/slices/Labeling/types'

import { LabelingObject, LabelingStatus } from '@/api/LabelingNode/types'

export const socketReducers = {
  // socket - object
  socketAddObjects: (state: Draft<LabelingState>, action: PayloadAction<{ itemId: string, objects: LabelingObject[] }>) => {
    const { itemId, objects } = action.payload

    const curLabelingItem = state.files[state.currentIndex]

    if (curLabelingItem?._id === itemId) {
      const transformedObjects = transformAnnotObjects(objects)
      const prevSnapshot = state.annotation[itemId]
      const newSnapshot = {
        status: prevSnapshot.status,
        object: [...prevSnapshot.object, ...transformedObjects],
      }

      addSnapshot(state, itemId, newSnapshot)
    }
  },
  socketDeleteObjects: (state: Draft<LabelingState>, action: PayloadAction<{ itemId: string, objects: LabelingObject[] }>) => {
    const { itemId, objects } = action.payload

    const curLabelingItem = state.files[state.currentIndex]

    if (curLabelingItem?._id === itemId) {
      const deletedObjectIds = map(objects, (o) => o._id)
      const prevSnapshot = state.annotation[itemId]
      const filtered = filter(prevSnapshot.object, (p) => !deletedObjectIds.includes(p.id || ''))
      const newSnapshot = { status: prevSnapshot.status, object: filtered }

      addSnapshot(state, itemId, newSnapshot)
    }
  },
  socketDeleteAllObjects: (state: Draft<LabelingState>, action: PayloadAction<{ itemId: string }>) => {
    const { itemId } = action.payload

    const curLabelingItem = state.files[state.currentIndex]

    if (curLabelingItem?._id === itemId) {
      const prevSnapshot = state.annotation[itemId]
      const newSnapshot = { status: prevSnapshot.status, object: [] }
      addSnapshot(state, itemId, newSnapshot)
    }
  },
  socketUpdateObjects: (state: Draft<LabelingState>, action: PayloadAction<{ itemId: string, objects: LabelingObjectData[] }>) => {
    const { itemId, objects } = action.payload

    const curLabelingItem = state.files[state.currentIndex]

    if (curLabelingItem?._id === itemId) {
      updateObjects(state, itemId, objects)
    }
  },
  socketStartLabelAssistant: (state: Draft<LabelingState>, action: PayloadAction<{ itemId: string }>) => {
    updateLabelAssistantStatus(state, { itemId: action.payload.itemId, isRunning: true })
  },
  socketFinishLabelAssistant: (state: Draft<LabelingState>, action: PayloadAction<{ itemId: string }>) => {
    updateLabelAssistantStatus(state, { itemId: action.payload.itemId, isRunning: false })
  },
  socketUndoRedo: (state: Draft<LabelingState>, action: PayloadAction<{ currentObjectsState: LabelingObject[] | null, prevObjectState: LabelingObject[] }>) => {
    undoRedoObject(state, action.payload)
  },
  // socket - labelingItem
  socketToggleItemStatus: (state: Draft<LabelingState>, action: PayloadAction<{ itemId: string, updatedStatus: LabelingStatus }>) => {
    updateLabelingItemStatus(state, action.payload)
  },
  // socket - comment
  socketCreateReviewObject: (state: Draft<LabelingState>, action: PayloadAction<LabelingObject>) => {
    createInitialCommentObject(state, action.payload)
  },
  socketAppendComment: (state: Draft<LabelingState>, action: PayloadAction<LabelingObject>) => {
    updateAppendComment(state, action.payload)
  },
  socketDeleteComment: (state: Draft<LabelingState>, action: PayloadAction<LabelingObject>) => {
    updateDeleteComment(state, action.payload)
  },
  socketToggleResolveComment: (state: Draft<LabelingState>, action: PayloadAction<LabelingObject>) => {
    updateToggleConfirmComment(state, action.payload)
  },
  socketConfirmAllComments: (state: Draft<LabelingState>, action: PayloadAction<{ labelingItemId: string, shouldCompleteReview: boolean }>) => {
    updateConfirmAllComments(state, action.payload)
  },
  // socket - classification tag
  socketAddTag: (state: Draft<LabelingState>, action: PayloadAction<{ itemId: string, labelingObjects: LabelingObject[] }>) => {
    const { itemId, labelingObjects } = action.payload
    const updatedIndex = findIndex(state.files, (f) => f?._id === itemId)
    if (updatedIndex !== -1) {
      set(state.files, `[${[updatedIndex]}].labelingObjects`, labelingObjects)
    }
  },
  socketDeleteTag: (state: Draft<LabelingState>, action: PayloadAction<{ tagId: string, itemId: string }>) => {
    const { tagId, itemId } = action.payload

    const updatedIndex = findIndex(state.files, (f) => f?._id === itemId)
    if (updatedIndex !== -1) {
      set(state.files, `[${[updatedIndex]}].labelingObjects`, filter(state.files[updatedIndex]?.labelingObjects, (o) => o._id !== tagId))
    }
  },
}
